import { useState, type ReactNode } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { Building2, Cloud, History, KeyRound, Pencil } from 'lucide-react';
import type { OperationalBranch, Pharmacy } from '@quds/shared';
import { useCan } from '@/auth/AuthContext';
import { CountryDisplay } from '@/components/location';
import {
  Breadcrumbs,
  Button,
  ButtonLink,
  Card,
  CardBody,
  CardHeader,
  DemoBadge,
  DescriptionList,
  EmptyState,
  ErrorState,
  LoadingState,
} from '@/components/ui';
import { getBusinessDetails, getPharmacy, listActivity, useQuery } from '@/data';
import { ActivityItem } from '@/features/activity/ActivityItem';
import { ApiError } from '@/lib/api';
import { formatDate, formatDateTime } from '@/lib/format';
import { CloudPosBadge } from '../components/CloudPosBadge';
import { ACTION_META } from '../actions/actionMeta';
import { usePharmacyActions } from '../actions/PharmacyActionsContext';
import { PharmacyActionsMenu } from '../actions/PharmacyActionsMenu';
import type { PharmacyAction } from '../actions/types';
import { DURATION_LABELS, LICENSE_TYPE_LABELS } from '../components/labels';
import { BusinessStatusBadge } from '../components/StatusBadges';
import { BranchDetailsModal } from './BranchDetailsModal';
import { BranchFormModal, type BranchFormContext } from './BranchFormModal';
import { BUSINESS_WORKSPACE_TABS, BusinessWorkspaceNav, type BusinessWorkspaceTab } from './BusinessWorkspaceNav';
import { LiveBusinessDetailsPanels } from './LiveBusinessDetailsPanels';
import { StartProductionSetupModal } from './StartProductionSetupModal';
import { UnifiedBranchesPanel } from './UnifiedBranchesPanel';

/** Keep in sync with BusinessWorkspaceNav — unknown ?tab= values fall back to overview. */
const TAB_IDS = new Set<string>(BUSINESS_WORKSPACE_TABS.map((t) => t.id));

export function PharmacyDetailsPage() {
  const { id = '' } = useParams();
  const { data: pharmacy, error, loading, reload } = useQuery(() => getPharmacy(id), [id]);

  if (loading) return <LoadingState label="Loading pharmacy…" />;
  if (error || !pharmacy) {
    const notFound = error instanceof ApiError && error.status === 404;
    return (
      <div className="card">
        <ErrorState
          title={notFound ? 'Pharmacy not found' : 'Could not load pharmacy'}
          message={notFound ? 'This pharmacy does not exist or was removed.' : (error?.message ?? 'Unknown error')}
          action={
            <div className="flex gap-2">
              {!notFound && (
                <Button variant="secondary" onClick={reload}>
                  Try again
                </Button>
              )}
              <ButtonLink to="/pharmacies">Back to Pharmacies</ButtonLink>
            </div>
          }
        />
      </div>
    );
  }

  return <PharmacyDetails pharmacy={pharmacy} onReload={reload} />;
}

function ManageButton({ action, pharmacy, children = 'Manage' }: { action: PharmacyAction; pharmacy: Pharmacy; children?: ReactNode }) {
  const { openAction } = usePharmacyActions();
  const can = useCan();
  if (!can(ACTION_META[action].permission)) return null;
  return (
    <Button variant="ghost" size="sm" onClick={() => openAction(action, { id: pharmacy.id, name: pharmacy.name })}>
      {children}
    </Button>
  );
}

function PharmacyDetails({ pharmacy, onReload }: { pharmacy: Pharmacy; onReload: () => void }) {
  const { openAction } = usePharmacyActions();
  const can = useCan();
  const [params, setParams] = useSearchParams();
  const tabParam = params.get('tab') ?? 'overview';
  const tab = (TAB_IDS.has(tabParam) ? tabParam : 'overview') as BusinessWorkspaceTab;
  const setTab = (next: BusinessWorkspaceTab) => {
    setParams(
      (prev) => {
        const sp = new URLSearchParams(prev);
        if (next === 'overview') sp.delete('tab');
        else sp.set('tab', next);
        return sp;
      },
      { replace: true },
    );
  };

  const [branchForm, setBranchForm] = useState<BranchFormContext | null>(null);
  const [branchDetails, setBranchDetails] = useState<OperationalBranch | null>(null);
  const [startProductionOpen, setStartProductionOpen] = useState(false);
  const canSeeActivity = can('activity.read');
  const canEditBranch = can('branch.update');
  const canSyncCloudBranchId = can('tenant.manage');
  const { data: activity } = useQuery(
    () => (canSeeActivity ? listActivity({ businessId: pharmacy.id, page: 1, pageSize: 5 }) : Promise.resolve(null)),
    [pharmacy.id, canSeeActivity],
  );
  const { data: liveDetails, loading: liveLoading, reload: reloadLive } = useQuery(() => getBusinessDetails(pharmacy.id), [pharmacy.id]);
  const { profile, branches } = pharmacy;
  const planned = profile.plannedLicense;
  const ref = { id: pharmacy.id, name: pharmacy.name };

  const cloudPos = liveDetails?.overview.cloudPosStatus ?? pharmacy.cloudPos?.status ?? 'not_connected';
  // Gate Cloud setup on CLOUD identity only — License/Updater "Linked" must not hide this.
  const canSetUpCloudPos = can('tenant.manage') && cloudPos === 'not_connected';

  const reloadAll = () => {
    onReload();
    void reloadLive();
  };

  return (
    <>
      <Breadcrumbs items={[{ label: 'Pharmacies', to: '/pharmacies' }, { label: pharmacy.name }]} />

      <div className="card mb-6 p-5 sm:p-6">
        <div className="flex flex-col gap-5 lg:flex-row lg:items-start lg:justify-between">
          <div className="flex min-w-0 items-start gap-4">
            <span className="hidden h-12 w-12 shrink-0 items-center justify-center rounded-lg bg-primary-soft text-primary sm:flex">
              <Building2 size={24} aria-hidden="true" />
            </span>
            <div className="min-w-0">
              <h1 className="flex flex-wrap items-center gap-2 text-[22px] font-semibold tracking-tight text-ink">
                {pharmacy.name}
                {pharmacy.isDemo && <DemoBadge />}
              </h1>
              <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-ink-secondary">
                <CountryDisplay country={pharmacy.country} city={pharmacy.city} flagSize={16} />
                <span className="font-mono text-ink-muted">{pharmacy.code}</span>
              </div>
              <div className="mt-3 flex flex-wrap items-center gap-2">
                <BusinessStatusBadge status={pharmacy.status} />
                <CloudPosBadge status={cloudPos} />
              </div>
            </div>
          </div>
          <div className="flex shrink-0 gap-2">
            {canSetUpCloudPos && (
              <Button variant="secondary" leftIcon={<Cloud size={16} aria-hidden="true" />} onClick={() => setStartProductionOpen(true)}>
                Set Up Cloud POS
              </Button>
            )}
            {can('pharmacy.update') && (
              <Button variant="secondary" leftIcon={<Pencil size={16} aria-hidden="true" />} onClick={() => openAction('edit', ref)}>
                Edit
              </Button>
            )}
            <PharmacyActionsMenu pharmacy={ref} variant="button" includeViewDetails={false} />
          </div>
        </div>
      </div>

      <BusinessWorkspaceNav active={tab} onChange={setTab} hiddenTabs={canSeeActivity ? undefined : ['activity']} />

      <div className="grid gap-6 xl:grid-cols-2">
        {tab === 'overview' && (
          <Card>
            <CardHeader title="Business Information" icon={<Building2 size={16} aria-hidden="true" />} actions={<ManageButton action="edit" pharmacy={pharmacy}>Edit</ManageButton>} />
            <CardBody>
              <DescriptionList
                items={[
                  { label: 'Pharmacy Name', value: pharmacy.name },
                  { label: 'Customer Code', value: <span className="font-mono">{pharmacy.code}</span> },
                  { label: 'Location', value: <CountryDisplay country={pharmacy.country} city={pharmacy.city} /> },
                  ...(pharmacy.administrativeAreaLevel1
                    ? [{ label: 'Division', value: pharmacy.administrativeAreaLevel1 }]
                    : []),
                  ...(pharmacy.area ? [{ label: 'Area', value: pharmacy.area }] : []),
                  ...(pharmacy.address ? [{ label: 'Address', value: pharmacy.address }] : []),
                  { label: 'Contact Person', value: pharmacy.contactPerson },
                  { label: 'Phone', value: <span className="tabular-nums">{pharmacy.phone}</span> },
                  { label: 'Email', value: pharmacy.email ?? '—' },
                  { label: 'Registration Number', value: profile.registrationNumber ?? '—' },
                  { label: 'Cloud POS', value: <CloudPosBadge status={cloudPos} /> },
                  { label: 'Customer Since', value: formatDate(pharmacy.createdAt) },
                  { label: 'Created By', value: pharmacy.createdBy?.displayName ?? (pharmacy.isDemo ? 'Demo seed' : '—') },
                  { label: 'Last Updated', value: formatDateTime(pharmacy.updatedAt) },
                  ...(pharmacy.notes ? [{ label: 'Notes', value: <span className="whitespace-pre-line">{pharmacy.notes}</span> }] : []),
                ]}
              />
              <details className="mt-4">
                <summary className="cursor-pointer text-xs font-medium text-ink-secondary hover:text-ink">Technical details</summary>
                <p className="mt-2 font-mono text-xs text-ink-muted break-all">Business ID: {pharmacy.id}</p>
              </details>
            </CardBody>
          </Card>
        )}

        {tab === 'license' && (
          <Card className="xl:col-span-2">
            <CardHeader title="License plan" icon={<KeyRound size={16} aria-hidden="true" />} actions={<ManageButton action="edit" pharmacy={pharmacy}>Edit</ManageButton>} />
            <CardBody>
              <p className="mb-4 text-xs text-ink-secondary">
                Planning metadata for sales and onboarding — not the installed POS license. Use the panels below for live
                POS license state and issued activation codes.
              </p>
              <DescriptionList
                columns={3}
                items={[
                  { label: 'Planned Type', value: planned ? LICENSE_TYPE_LABELS[planned.type] : '—' },
                  { label: 'Planned Duration', value: planned ? (planned.duration ? DURATION_LABELS[planned.duration] : 'No expiration') : '—' },
                  { label: 'Planned Expiration', value: planned ? (planned.expiresOn ? formatDate(planned.expiresOn) : 'No expiration') : '—' },
                ]}
              />
            </CardBody>
          </Card>
        )}

        {tab === 'branches' && (
          <Card className="xl:col-span-2">
            <CardBody>
              <UnifiedBranchesPanel
                details={liveDetails ?? null}
                businessId={pharmacy.id}
                loading={liveLoading}
                onAddBranch={() => setBranchForm({ branch: null, productionBranchId: null })}
                onOpenBranch={(row) => setBranchDetails(row)}
                onSetUpCloudPos={() => setStartProductionOpen(true)}
                onRefresh={reloadAll}
              />
            </CardBody>
          </Card>
        )}

        {tab === 'activity' && canSeeActivity && (
          <Card className="xl:col-span-2">
            <CardHeader
              title="Recent Activity"
              icon={<History size={16} aria-hidden="true" />}
              actions={<ManageButton action="activity" pharmacy={pharmacy}>View all</ManageButton>}
            />
            <CardBody className="py-1">
              {!activity ? (
                <LoadingState className="py-8" />
              ) : activity.items.length === 0 ? (
                <EmptyState compact title="No activity yet" />
              ) : (
                <ul className="divide-y divide-line">
                  {activity.items.map((e) => (
                    <ActivityItem key={e.id} event={e} showTarget={false} />
                  ))}
                </ul>
              )}
            </CardBody>
          </Card>
        )}

        <LiveBusinessDetailsPanels
          details={liveDetails ?? null}
          loading={liveLoading}
          businessId={pharmacy.id}
          onRefresh={() => void reloadLive()}
          tab={tab}
          onRequestTab={setTab}
          onSetUpCloudPos={() => setStartProductionOpen(true)}
          hideLegacyBranchPanels
        />
      </div>

      {branchDetails && (
        <BranchDetailsModal
          operational={branchDetails}
          branch={
            branchDetails.caBranchId
              ? (branches.find((b) => b.id === branchDetails.caBranchId) ?? null)
              : null
          }
          canEdit={canEditBranch && Boolean(branchDetails.caBranchId)}
          onClose={() => setBranchDetails(null)}
          onEdit={() => {
            const ca = branchDetails.caBranchId
              ? (branches.find((b) => b.id === branchDetails.caBranchId) ?? null)
              : null;
            if (!ca) return;
            setBranchDetails(null);
            setBranchForm({
              branch: ca,
              productionBranchId: branchDetails.productionBranchId,
            });
          }}
        />
      )}
      {branchForm && (
        <BranchFormModal
          pharmacy={pharmacy}
          context={branchForm}
          cloudPosStatus={cloudPos}
          canSyncCloudBranchId={canSyncCloudBranchId}
          onClose={() => {
            setBranchForm(null);
            reloadAll();
          }}
        />
      )}
      {startProductionOpen && (
        <StartProductionSetupModal
          businessId={pharmacy.id}
          businessName={pharmacy.name}
          defaultBranchName={branches.find((b) => b.type === 'main')?.name ?? branches[0]?.name}
          defaultBranchRef={branches.find((b) => b.type === 'main')?.referenceCode ?? branches[0]?.referenceCode}
          existingCaBranches={branches.map((b) => ({ id: b.id, name: b.name, referenceCode: b.referenceCode }))}
          onClose={() => setStartProductionOpen(false)}
          onDone={reloadAll}
        />
      )}
    </>
  );
}
